
import { snowflakeApi } from '../utils/api';
import workflowService from './api/workflowService';
import { getClientIdFromUrl } from '../utils/clientId';

// --------------------------
// Simplified Module 2 Types
// --------------------------

export interface ClassificationRequest {
  topics: {
    [key: string]: {
      subtopics: {
        [key: string]: string[]
      }
    }
  };
  keywords: string[];
  client_id?: string;
  workflow_id?: string;
}

export interface KeywordResult {
  keyword: string;
  topic: string;
  subtopic: string;
  relevance: number;
  confidence: number;
  location: boolean;
}

export interface ClassificationResults {
  relevant: KeywordResult[];
  undecided: KeywordResult[];
  outlier: KeywordResult[];
  statistics: {
    total: number;
    relevant: number;
    undecided: number;
    outlier: number;
  };
}

export interface ClassificationResponse {
  classification_id: string;
  workflow_id: string;
  status: 'created' | 'processing' | 'completed' | 'failed';
  results?: ClassificationResults;
  error?: string;
}

export interface ClassificationStatus {
  classification_id: string;
  workflow_id: string;
  status: 'created' | 'processing' | 'completed' | 'failed';
  progress?: number;
  results?: ClassificationResults;
  error?: string;
}

// --------------------------
// Legacy Code Generation Types
// --------------------------
export interface CodeGenerationRequest {
  workflow_id: string;
  stem_words_blob: string;
  sample_keywords?: string[];
  client_id?: string;
  model?: string;
  language?: string;
  code_type?: string;
  topic_framework_blob?: string;
}

export interface CodeGenerationStatus {
  status: 'created' | 'processing' | 'completed' | 'failed';
  message?: string;
  error?: string;
  generation_id?: string;
  code_blob?: string;
  code?: string;
  requirements_blob?: string;
}

export interface CodeExecutionRequest {
  workflow_id: string;
  generation_id: string;
  keywords_blob: string;
}

export interface CodeExecutionResult {
  execution_id: string;
  workflow_id: string;
  generation_id?: string;
  status: 'created' | 'processing' | 'completed' | 'failed';
  results_blobs?: Record<string, string>;
  result_stats?: Record<string, number>;
  output?: string;
  error?: string;
}

interface TestResult {
  name: string;
  status: 'passed' | 'failed' | 'running';
  error?: string;
}

interface CodeReview {
  code: string;
  testResults: TestResult[];
  status: 'draft' | 'reviewed' | 'approved';
}

// Database-first interfaces
interface DbCodeGenerationRequest {
  workflowId: number;
  stemWordsBlob: string;
  sampleKeywords?: string[];
  clientId?: number;
}

interface DbCodeExecutionRequest {
  workflowId: number;
  moduleId: number;
  keywordsBlob: string;
  clientId?: number;
}

// Legacy interfaces for backward compatibility
interface DbCodeExecutionByModuleRequest {
  moduleId: number;
  keywordsBlob: string;
}

// API version prefix - try different versions
const API_V1 = '/api/v1';
const API_ROOT = ''; // No prefix

// Service implementation
class Module2Service {
  private static instance: Module2Service;
  
  private constructor() {
    // Private constructor for singleton pattern
    // Log the base URL being used
    console.log('Module2Service initialized with base URL:', 
      (snowflakeApi.defaults.baseURL || 'undefined'));
  }
  
  public static getInstance(): Module2Service {
    if (!Module2Service.instance) {
      Module2Service.instance = new Module2Service();
    }
    return Module2Service.instance;
  }
  
  // Helper method to get Application Insights
  private getAppInsights() {
    return (window as any).__appInsights || null;
  }
  
  // --------------------------------
  // Simplified Module 2 Methods
  // --------------------------------
  
  /**
   * Parse keywords from CSV content
   */
  parseKeywordsFromCsv(csvContent: string): string[] {
    // Parse CSV content - skip header row if present
    const lines = csvContent.split('\n');
    const keywords: string[] = [];
    
    // Check if first line is header (contains "Query")
    const startIndex = lines[0].includes("Query") ? 1 : 0;
    
    // Extract keywords from each line
    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line) {
        // If the line contains commas, it's likely a CSV with multiple columns
        // So we take the first column as the keyword
        if (line.includes(',')) {
          const parts = line.split(',');
          keywords.push(parts[0].trim());
        } else {
          keywords.push(line);
        }
      }
    }
    
    console.log(`Parsed ${keywords.length} keywords from CSV`);
    return keywords;
  }

  /**
   * Transform Module 1 output to Module 2 input format
   */
  prepareClassificationRequest(
    topicsData: any, 
    keywords: string[],
    clientId?: string
  ): ClassificationRequest {
    // Extract client ID from the topics data if available
    const effectiveClientId = clientId || topicsData.client_id || getClientIdFromUrl();
    
    // Extract workflow_id from topicsData if available
    let workflowId = topicsData.workflow_id;
    
    // Extract numeric workflow ID if it's in the format "wf-12345-hash"
    if (workflowId && workflowId.startsWith("wf-")) {
      const match = workflowId.match(/wf-(\d+)/);
      if (match && match[1]) {
        console.log(`Extracted numeric workflow ID '${match[1]}' from '${workflowId}'`);
        workflowId = match[1];
      }
    }
    
    console.log('Preparing classification request with topics data:', 
      JSON.stringify(topicsData, null, 2));
    
    // Added debug logging to see what we're sending to the backend
    const request = {
      topics: topicsData.topics, // Use topicsData.topics directly (no extra nesting)
      keywords: keywords,
      client_id: effectiveClientId,
      workflow_id: workflowId // Include workflow_id if available
    };
    
    console.log('Final request structure:', JSON.stringify(request, null, 2));
    
    return request;
  }

  /**
   * Fetch content from blob storage
   */
  async fetchBlobContent(blobPath: string, clientId?: string): Promise<any> {
    try {
      console.log(`Fetching blob content from ${blobPath}`);
      
      // Extract client ID from path if possible and not provided
      if (!clientId && blobPath.includes('/')) {
        const parts = blobPath.split('/');
        // If path follows pattern clientId/workflows/...
        const workflowsIndex = parts.indexOf('workflows');
        if (workflowsIndex > 0) {
          clientId = parts[workflowsIndex - 1];
          console.log(`Extracted client_id: ${clientId} from blob path`);
        }
      }
      
      // Try to determine if this is a full path with container name
      let container = 'client-data';
      let adjustedPath = blobPath;
      
      // If path starts with container name, separate it out
      if (blobPath.startsWith('client-data/')) {
        adjustedPath = blobPath.substring('client-data/'.length);
        console.log(`Adjusted path: ${adjustedPath}`);
      }
      
      // Create params object with all needed parameters
      const params: any = { 
        path: adjustedPath,
        container
      };
      
      // Only add client_id if we have it
      if (clientId) {
        params.client_id = clientId;
        console.log(`Adding client_id: ${clientId} to blob fetch request`);
      }
      
      console.log(`Fetching blob with params:`, params);
      const response = await snowflakeApi.get(`/api/v1/module2/storage/get`, { params });
      return response.data;
    } catch (error: any) {
      console.error(`Error fetching blob content from ${blobPath}:`, error);
      throw error;
    }
  }
  
  /**
   * Classify keywords using simplified Module 2
   */
  async classifyKeywords(
    topics: any,            // The actual topics data
    keywords: string[],     // The actual keywords array
    workflowId: string,
    clientId?: string
  ): Promise<ClassificationResponse> {
    console.log('Classifying keywords using simplified Module 2');
    
    // Extract numeric workflow ID if it's in the format "wf-12345-hash"
    let numericWorkflowId = workflowId;
    if (workflowId && workflowId.startsWith("wf-")) {
      const match = workflowId.match(/wf-(\d+)/);
      if (match && match[1]) {
        console.log(`Extracted numeric workflow ID '${match[1]}' from '${workflowId}'`);
        numericWorkflowId = match[1];
      }
    }
    
    // Track classification start in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `module2-classify-${numericWorkflowId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'Module2Simplified_ClassifyStarted',
        properties: {
          correlationId,
          workflowId: numericWorkflowId, // Use the numeric workflow ID
          originalWorkflowId: workflowId, // Also track the original ID
          clientId: clientId || 'unknown',
          keywordsCount: keywords?.length || 0,
          topicsCount: Object.keys(topics || {}).length,
          directIntegration: true, // Flag to indicate this is a direct integration, not using blob paths
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      // No need to fetch data from blob storage since we're receiving it directly
      console.log(`Using direct topics and keywords data`);
      
      // Prepare the classification request
      const request = {
        topics: topics,
        keywords: keywords,
        client_id: clientId,
        workflow_id: numericWorkflowId
      };
      
      console.log(`Request prepared with ${keywords.length} keywords and topics data`);
      
      // Add debug logs for request structure
      console.log('Classification request structure:', 
        JSON.stringify({
          topics_structure: Object.keys(request.topics || {}),
          keywords_length: request.keywords?.length,
          client_id: request.client_id,
          workflow_id: request.workflow_id
        }, null, 2)
      );
      
      // 5. Send the classification request
      console.log('Sending classification request to standard endpoint:', request);
      
      try {
        // Use direct-classify-v4 for simplified file handling
        const response = await snowflakeApi.post<ClassificationResponse>('/api/v1/direct-classify-v4', request);
        
        console.log('Classification succeeded with data:', response.data);
        
        // Track successful classification in Application Insights
        if (appInsights) {
          const duration = Date.now() - startTime;
          appInsights.trackEvent({
            name: 'Module2Simplified_ClassifyCompleted',
            properties: {
              correlationId,
              workflowId: numericWorkflowId,
              originalWorkflowId: workflowId,
              clientId: clientId || 'unknown',
              classificationId: response.data.classification_id,
              status: response.data.status,
              keywordsCount: keywords?.length || 0,
              topicsCount: Object.keys(topics || {}).length,
              directIntegration: true, // Flag to indicate this is a direct integration
              durationMs: duration,
              timestamp: new Date().toISOString()
            }
          });
        }
        
        return response.data;
      } catch (error) {
        console.error('Error classifying keywords:', error);
        
        // Track error in Application Insights but don't provide fallback
        if (appInsights) {
          const duration = Date.now() - startTime;
          appInsights.trackException({
            exception: error instanceof Error ? error : new Error('Unknown error in classify keywords'),
            properties: {
              correlationId,
              workflowId: numericWorkflowId,
              originalWorkflowId: workflowId,
              clientId: clientId || 'unknown',
              operation: 'classifyKeywords',
              errorMessage: error instanceof Error ? error.message : String(error),
              timestamp: new Date().toISOString()
            }
          });
        }
        
        // Re-throw the error to be handled by the caller
        throw error;
      }
    } catch (error: any) {
      console.error('Error classifying keywords:', error);
      
      // Track error in Application Insights
      if (appInsights) {
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in classify keywords'),
          properties: {
            correlationId,
            workflowId: numericWorkflowId,
            originalWorkflowId: workflowId,
            clientId: clientId || 'unknown',
            operation: 'classifyKeywords',
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }
  
  /**
   * Get classification status
   */
  async getClassificationStatus(
    classificationId: string,
    workflowId: string
  ): Promise<ClassificationStatus> {
    console.log(`Getting classification status for ${classificationId}`);
    
    try {
      const response = await snowflakeApi.get<ClassificationStatus>(
        `/api/v1/classifications/${classificationId}`,
        { params: { workflow_id: workflowId } }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting classification status:', error);
      throw error;
    }
  }
  
  /**
   * List classification output files
   */
  async listClassificationFiles(workflowId: string, clientId?: string): Promise<any> {
    console.log(`Listing classification files for workflow ${workflowId}`);
    
    try {
      const effectiveClientId = clientId || getClientIdFromUrl();
      
      const response = await snowflakeApi.get('/api/v1/files', {
        params: {
          workflow_id: workflowId,
          client_id: effectiveClientId
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error listing classification files:', error);
      throw error;
    }
  }
  
  async getHealth() {
    try {
      // Use Snowflake BFF proxy endpoint for Module 2 health check
      console.log('Performing health check for module2-simplified via Snowflake BFF');
      // Try multiple possible health endpoint paths
      try {
        // First try the DB API health endpoint via Snowflake proxy
        const response = await snowflakeApi.get('/api/v1/db/health');
        console.log('Health check successful via DB API endpoint');
        return response.data;
      } catch (initialError) {
        console.log('DB API health endpoint failed, trying Module 2 proxy health endpoint');
        // Fall back to Module 2 proxy health endpoint
        const response = await snowflakeApi.get('/api/v1/module2/health');
        console.log('Health check successful via Module 2 proxy endpoint');
        return response.data;
      }
    } catch (error) {
      console.error('All health check attempts failed:', error);
      return { status: 'failed', error: 'Health check failed' };
    }
  }
  
  // Method to generate code
  async generateCode(request: CodeGenerationRequest): Promise<CodeGenerationStatus> {
    try {
      // Log the base URL and request for debugging
      const baseUrl = snowflakeApi.defaults.baseURL || '';
      console.log('Module2 API Base URL:', baseUrl);
      console.log('Request payload:', JSON.stringify(request, null, 2));
      
      // Track code generation start in Application Insights
      const appInsights = this.getAppInsights();
      const startTime = Date.now();
      const correlationId = `module2-generate-${request.workflow_id}-${Date.now()}`;
      
      if (appInsights) {
        appInsights.trackEvent({
          name: 'Module2API_GenerateCodeStarted',
          properties: {
            correlationId,
            workflowId: request.workflow_id,
            clientId: request.client_id || 'default',
            operation: 'generateCode',
            timestamp: new Date().toISOString()
          }
        });
      }
      
      // Ensure stem_words_blob has the correct prefix
      if (request.stem_words_blob && request.stem_words_blob.startsWith('client-data/')) {
        // Remove the prefix as the API validation expects paths without it
        request.stem_words_blob = request.stem_words_blob.replace('client-data/', '');
        console.log('Removed client-data/ prefix from stem_words_blob to match API validation');
      }
      
      // Update client_id in request as needed
      if (!request.client_id && request.workflow_id) {
        // Try to extract from stem_words_blob path
        const parts = request.stem_words_blob.split('/');
        const clientIndex = parts.indexOf('workflows') - 1;
        if (clientIndex > 0) {
          request.client_id = parts[clientIndex];
          console.log(`Extracted client_id: ${request.client_id} from stem_words_blob`);
        }
        
        // If still no client_id, try to get it from URL path
        if (!request.client_id) {
          const urlParts = window.location.pathname.split('/');
          const clientIdIndex = urlParts.indexOf('client') + 1;
          
          if (clientIdIndex > 0 && clientIdIndex < urlParts.length) {
            request.client_id = urlParts[clientIdIndex];
            console.log(`Extracted client_id: ${request.client_id} from URL path`);
          }
        }
        
        // Store client_id in localStorage if found
        if (request.client_id) {
          try {
            localStorage.setItem('selected_client_id', request.client_id);
            console.log(`Stored client_id ${request.client_id} in localStorage`);
          } catch (e) {
            console.warn('Failed to store client_id in localStorage:', e);
          }
        }
      }
      
      console.log('Updated request:', JSON.stringify(request, null, 2));
      
      try {
        // Create a complete request object - use the same pattern as module1Service
        const generateParams = {
          workflow_id: request.workflow_id,
          stem_words_blob: request.stem_words_blob,
          sample_keywords: request.sample_keywords,
          client_id: request.client_id || 'default', // Always include client_id, default as fallback
          language: request.language,
          code_type: request.code_type,
          topic_framework_blob: request.topic_framework_blob
        };
        
        // Use a simple URL without query parameters
        const url = `/api/v1/generate/`;
        console.log(`Calling API at: ${url} with params:`, generateParams);
        const response = await snowflakeApi.post(url, generateParams);
        console.log('API call successful');
        
        // Track successful code generation in Application Insights
        if (appInsights) {
          const duration = Date.now() - startTime;
          appInsights.trackEvent({
            name: 'Module2API_GenerateCodeCompleted',
            properties: {
              correlationId,
              workflowId: request.workflow_id,
              clientId: request.client_id || 'default',
              operation: 'generateCode',
              responseStatus: response.data?.status || 'unknown',
              durationMs: duration,
              hasCode: !!response.data?.code || !!response.data?.code_blob,
              timestamp: new Date().toISOString()
            }
          });
        }
        
        return response.data;
      } catch (err: any) {
        console.error('API call failed:', err);
        
        // Track error in Application Insights
        if (appInsights) {
          const duration = Date.now() - startTime;
          appInsights.trackException({
            exception: err instanceof Error ? err : new Error('Unknown error in generate code'),
            properties: {
              correlationId,
              workflowId: request.workflow_id,
              clientId: request.client_id || 'default',
              operation: 'generateCode',
              durationMs: duration,
              errorType: err instanceof Error ? err.name : typeof err,
              errorMessage: err.response?.data?.detail || 
                           (err instanceof Error ? err.message : String(err)),
              timestamp: new Date().toISOString()
            }
          });
        }
        
        // Enhanced error logging to help diagnose the issue
        if (err.response) {
          // The request was made and the server responded with an error
          console.error('Error response data:', err.response.data);
          console.error('Error response status:', err.response.status);
          console.error('Error response headers:', err.response.headers);
          
          // If we have validation error details
          if (err.response.data && err.response.data.detail) {
            return {
              status: 'failed',
              error: `Validation error: ${JSON.stringify(err.response.data.detail)}`
            };
          }
        } else if (err.request) {
          // The request was made but no response was received
          console.error('No response received:', err.request);
        } else {
          // Something happened in setting up the request
          console.error('Request setup error:', err.message);
        }
        
        throw err;
      }
    } catch (error) {
      console.error('Code generation failed:', error);
      return {
        status: 'failed',
        error: error instanceof Error ? error.message : 'Code generation failed'
      };
    }
  }
  
  // Method to check code generation status
  async getGenerationStatus(generationId: string, workflowId: string, clientId?: string): Promise<CodeGenerationStatus> {
    try {
      // Track status check in Application Insights
      const appInsights = this.getAppInsights();
      const startTime = Date.now();
      const correlationId = `module2-status-${generationId}-${Date.now()}`;
      
      // Extract client ID using the same logic as generateCode if not provided
      if (!clientId) {
        // First try to extract from localStorage
        try {
          clientId = localStorage.getItem('selected_client_id') || undefined;
          if (clientId) {
            console.log(`Retrieved client_id ${clientId} from localStorage`);
          }
        } catch (e) {
          console.warn('Failed to get client_id from localStorage:', e);
        }
        
        // If still no client ID, try to extract from URL path
        if (!clientId) {
          const urlParts = window.location.pathname.split('/');
          const clientIdIndex = urlParts.indexOf('client') + 1;
          
          if (clientIdIndex > 0 && clientIdIndex < urlParts.length) {
            clientId = urlParts[clientIdIndex];
            console.log(`Extracted client_id: ${clientId} from URL path`);
          }
        }
        
        // If still no client ID and we have a workflowId that contains client info
        if (!clientId && workflowId) {
          // If the workflow ID path contains client info (common pattern in our app)
          if (workflowId.includes('/')) {
            const parts = workflowId.split('/');
            // Look for client ID before "workflows" in the path
            const clientIndex = parts.indexOf('workflows') - 1;
            if (clientIndex > 0) {
              clientId = parts[clientIndex];
              console.log(`Extracted client_id: ${clientId} from workflow_id path`);
            }
          }
        }
        
        // If still no client ID, extract from the first part of workflow ID
        // This is a common convention: client ID is often first segment of workflow ID
        if (!clientId && workflowId) {
          const workflowParts = workflowId.split('-');
          if (workflowParts.length > 0 && workflowParts[0].length > 0) {
            // Check if the first segment matches expected client ID pattern
            if (/^[a-zA-Z]/.test(workflowParts[0])) {
              clientId = workflowParts[0];
              console.log(`Extracted potential client_id: ${clientId} from workflow_id first segment`);
            }
          }
        }
      }
      
      // Use default as fallback if we still don't have a client ID
      if (!clientId) {
        clientId = 'default';
        console.log(`Using default client_id since none could be extracted`);
      }
      
      // Log extracted client ID for debugging
      console.log(`Using client_id: ${clientId} for generation status check`);
      
      if (appInsights) {
        appInsights.trackEvent({
          name: 'Module2API_StatusCheckStarted',
          properties: {
            correlationId,
            workflowId,
            generationId,
            clientId,
            operation: 'getGenerationStatus',
            timestamp: new Date().toISOString()
          }
        });
      }
      
      // Use params object for query parameters to be consistent
      const params = { 
        workflow_id: workflowId,
        client_id: clientId
      };
      
      console.log(`Checking generation status: ${generationId} with params:`, params);
      const response = await snowflakeApi.get(`${API_V1}/generate/${generationId}`, { params });
      
      // Track successful status check in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'Module2API_StatusCheckCompleted',
          properties: {
            correlationId,
            workflowId,
            generationId,
            operation: 'getGenerationStatus',
            responseStatus: response.data?.status || 'unknown',
            durationMs: duration,
            hasCode: !!response.data?.code || !!response.data?.code_blob,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Failed to get generation status:', error);
      
      // Track error in Application Insights
      const appInsights = this.getAppInsights();
      if (appInsights) {
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in get generation status'),
          properties: {
            workflowId,
            generationId,
            operation: 'getGenerationStatus',
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return {
        status: 'failed',
        error: error instanceof Error ? error.message : 'Failed to get generation status'
      };
    }
  }
  
  // Method to execute code
  async executeCode(request: CodeExecutionRequest): Promise<CodeExecutionResult> {
    try {
      // Track code execution request in Application Insights
      const appInsights = this.getAppInsights();
      const startTime = Date.now();
      const correlationId = `module2-execute-${request.workflow_id}-${request.generation_id}-${Date.now()}`;
      
      if (appInsights) {
        appInsights.trackEvent({
          name: 'Module2API_ExecuteCodeStarted',
          properties: {
            correlationId,
            workflowId: request.workflow_id,
            generationId: request.generation_id,
            operation: 'executeCode',
            timestamp: new Date().toISOString()
          }
        });
      }
      
      // Ensure keywords_blob has the correct format
      let keywordsBlob = request.keywords_blob;
      if (keywordsBlob.startsWith('client-data/')) {
        // Remove the prefix as the API validation expects paths without it
        keywordsBlob = keywordsBlob.replace('client-data/', '');
        console.log('Removed client-data/ prefix from keywords_blob to match API validation');
      }
      
      // Try to extract client ID from various sources
      let clientId: string | undefined;
      
      // First try to extract from localStorage
      try {
        clientId = localStorage.getItem('selected_client_id') || undefined;
        if (clientId) {
          console.log(`Retrieved client_id ${clientId} from localStorage for code execution`);
        }
      } catch (e) {
        console.warn('Failed to get client_id from localStorage:', e);
      }
      
      // If still no client ID, try to extract from URL path
      if (!clientId) {
        const urlParts = window.location.pathname.split('/');
        const clientIdIndex = urlParts.indexOf('client') + 1;
        
        if (clientIdIndex > 0 && clientIdIndex < urlParts.length) {
          clientId = urlParts[clientIdIndex];
          console.log(`Extracted client_id: ${clientId} from URL path for code execution`);
        }
      }
      
      // If still no client ID, try to extract from keywords_blob or workflow_id
      if (!clientId) {
        // First check if keywords_blob contains client info
        if (keywordsBlob.includes('/')) {
          const parts = keywordsBlob.split('/');
          const clientIndex = parts.indexOf('workflows') - 1;
          if (clientIndex > 0) {
            clientId = parts[clientIndex];
            console.log(`Extracted client_id: ${clientId} from keywords_blob for code execution`);
          }
        }
        
        // If still no client ID and we have a workflowId
        if (!clientId && request.workflow_id) {
          // Check if workflow ID is a path containing client ID
          if (request.workflow_id.includes('/')) {
            const parts = request.workflow_id.split('/');
            const clientIndex = parts.indexOf('workflows') - 1;
            if (clientIndex > 0) {
              clientId = parts[clientIndex];
              console.log(`Extracted client_id: ${clientId} from workflow_id path for code execution`);
            }
          } else {
            // Try extracting from workflow ID pattern (first segment if it looks like a client ID)
            const workflowParts = request.workflow_id.split('-');
            if (workflowParts.length > 0 && workflowParts[0].length > 0) {
              if (/^[a-zA-Z]/.test(workflowParts[0])) {
                clientId = workflowParts[0];
                console.log(`Extracted potential client_id: ${clientId} from workflow_id first segment for code execution`);
              }
            }
          }
        }
      }
      
      // Use default as fallback if we still don't have a client ID
      if (!clientId) {
        clientId = 'default';
        console.log(`Using default client_id since none could be extracted for code execution`);
      }
      
      // Create a consistent request object
      const executeParams = {
        workflow_id: request.workflow_id,
        generation_id: request.generation_id,
        keywords_blob: keywordsBlob,
        client_id: clientId // Use extracted client ID
      };
      
      // Log the client ID being used
      console.log(`Using client_id: ${clientId} for code execution`);
      
      console.log(`Executing code with params:`, executeParams);
      const response = await snowflakeApi.post(`${API_V1}/execute/`, executeParams);
      
      // Track successful code execution in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'Module2API_ExecuteCodeCompleted',
          properties: {
            correlationId,
            workflowId: request.workflow_id,
            generationId: request.generation_id,
            operation: 'executeCode',
            responseStatus: response.data?.status || 'unknown',
            durationMs: duration,
            hasOutput: !!response.data?.output,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Code execution failed:', error);
      
      // Track error in Application Insights
      const appInsights = this.getAppInsights();
      if (appInsights) {
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in execute code'),
          properties: {
            workflowId: request.workflow_id,
            generationId: request.generation_id,
            operation: 'executeCode',
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return {
        execution_id: 'failed',
        workflow_id: request.workflow_id,
        generation_id: request.generation_id,
        status: 'failed',
        error: error instanceof Error ? error.message : 'Code execution failed'
      };
    }
  }
  
  // Method to get execution status
  async getExecutionStatus(executionId: string, workflowId: string, clientId?: string): Promise<CodeExecutionResult> {
    try {
      // Track execution status check in Application Insights
      const appInsights = this.getAppInsights();
      const startTime = Date.now();
      const correlationId = `module2-exec-status-${executionId}-${Date.now()}`;
      
      // Extract client ID using the same logic as generateCode if not provided
      if (!clientId) {
        // First try to extract from localStorage
        try {
          clientId = localStorage.getItem('selected_client_id') || undefined;
          if (clientId) {
            console.log(`Retrieved client_id ${clientId} from localStorage for execution status check`);
          }
        } catch (e) {
          console.warn('Failed to get client_id from localStorage:', e);
        }
        
        // If still no client ID, try to extract from URL path
        if (!clientId) {
          const urlParts = window.location.pathname.split('/');
          const clientIdIndex = urlParts.indexOf('client') + 1;
          
          if (clientIdIndex > 0 && clientIdIndex < urlParts.length) {
            clientId = urlParts[clientIdIndex];
            console.log(`Extracted client_id: ${clientId} from URL path for execution status check`);
          }
        }
        
        // If we have a workflowId that contains client info
        if (!clientId && workflowId) {
          // Check if the path contains the client ID (common pattern in our app)
          if (workflowId.includes('/')) {
            const parts = workflowId.split('/');
            // Client ID is typically before "workflows" in the path
            const clientIndex = parts.indexOf('workflows') - 1;
            if (clientIndex > 0) {
              clientId = parts[clientIndex];
              console.log(`Extracted client_id: ${clientId} from workflow_id path for execution status check`);
            }
          }
        }
        
        // If still no client ID, try to extract from workflow ID pattern
        if (!clientId && workflowId) {
          // Check if the workflow ID starts with a client ID segment
          const workflowParts = workflowId.split('-');
          if (workflowParts.length > 0 && workflowParts[0].length > 0) {
            // Check if first segment matches expected client ID pattern
            if (/^[a-zA-Z]/.test(workflowParts[0])) {
              clientId = workflowParts[0];
              console.log(`Extracted potential client_id: ${clientId} from workflow_id first segment for execution status check`);
            }
          }
        }
      }
      
      // Use default as fallback if we still don't have a client ID
      if (!clientId) {
        clientId = 'default';
        console.log(`Using default client_id since none could be extracted for execution status check`);
      }
      
      // Log extracted client ID for debugging
      console.log(`Using client_id: ${clientId} for execution status check`);
      
      if (appInsights) {
        appInsights.trackEvent({
          name: 'Module2API_ExecutionStatusCheckStarted',
          properties: {
            correlationId,
            workflowId,
            executionId,
            clientId,
            operation: 'getExecutionStatus',
            timestamp: new Date().toISOString()
          }
        });
      }
      
      // Use params object for query parameters to be consistent
      const params = {
        workflow_id: workflowId,
        client_id: clientId
      };
      
      console.log(`Checking execution status: ${executionId} with params:`, params);
      const response = await snowflakeApi.get(`${API_V1}/execute/${executionId}`, { params });
      
      // Track successful execution status check in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'Module2API_ExecutionStatusCheckCompleted',
          properties: {
            correlationId,
            workflowId,
            executionId,
            operation: 'getExecutionStatus',
            responseStatus: response.data?.status || 'unknown',
            durationMs: duration,
            hasOutput: !!response.data?.output,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Failed to get execution status:', error);
      
      // Track error in Application Insights
      const appInsights = this.getAppInsights();
      if (appInsights) {
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in get execution status'),
          properties: {
            workflowId,
            executionId,
            operation: 'getExecutionStatus',
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return {
        execution_id: executionId,
        workflow_id: '',
        status: 'failed',
        error: error instanceof Error ? error.message : 'Failed to get execution status'
      };
    }
  }
  
  // Method to get generated code
  async getGeneratedCode(workflowId: string, generationId: string, clientId?: string): Promise<CodeReview> {
    try {
      // Extract client ID using the same logic as other methods if not provided
      if (!clientId) {
        // First try to extract from localStorage
        try {
          clientId = localStorage.getItem('selected_client_id') || undefined;
          if (clientId) {
            console.log(`Retrieved client_id ${clientId} from localStorage for getting generated code`);
          }
        } catch (e) {
          console.warn('Failed to get client_id from localStorage:', e);
        }
        
        // If still no client ID, try to extract from URL path
        if (!clientId) {
          const urlParts = window.location.pathname.split('/');
          const clientIdIndex = urlParts.indexOf('client') + 1;
          
          if (clientIdIndex > 0 && clientIdIndex < urlParts.length) {
            clientId = urlParts[clientIdIndex];
            console.log(`Extracted client_id: ${clientId} from URL path for getting generated code`);
          }
        }
        
        // If still no client ID and we have a workflowId 
        if (!clientId && workflowId) {
          // Check if the path contains client info
          if (workflowId.includes('/')) {
            const parts = workflowId.split('/');
            const clientIndex = parts.indexOf('workflows') - 1;
            if (clientIndex > 0) {
              clientId = parts[clientIndex];
              console.log(`Extracted client_id: ${clientId} from workflow_id path for getting generated code`);
            }
          } else {
            // Try to extract from workflow ID pattern
            const workflowParts = workflowId.split('-');
            if (workflowParts.length > 0 && workflowParts[0].length > 0) {
              if (/^[a-zA-Z]/.test(workflowParts[0])) {
                clientId = workflowParts[0];
                console.log(`Extracted potential client_id: ${clientId} from workflow_id first segment for getting generated code`);
              }
            }
          }
        }
      }
      
      // Use default as fallback if we still don't have a client ID
      if (!clientId) {
        clientId = 'default';
        console.log(`Using default client_id since none could be extracted for getting generated code`);
      }
      
      // Log the client ID being used
      console.log(`Using client_id: ${clientId} for getting generated code`);
      
      // Use params object for query parameters to be consistent
      const params = {
        workflow_id: workflowId,
        client_id: clientId
      };
      
      console.log(`Getting generated code for: ${generationId} with params:`, params);
      const response = await snowflakeApi.get(`${API_V1}/generate/${generationId}/code`, { params });
      return {
        code: response.data.code || '',
        testResults: [],
        status: 'draft'
      };
    } catch (error) {
      console.error('Failed to get generated code:', error);
      return {
        code: "// Failed to retrieve code",
        testResults: [],
        status: 'draft'
      };
    }
  }

  // DATABASE-FIRST METHODS
  
  // Generate code using database workflow ID
  async generateCodeDb(request: DbCodeGenerationRequest): Promise<any> {
    console.log('Generating code with database workflow ID:', request.workflowId);
    
    // Track API call start in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `db-codegen-${request.workflowId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_CodeGenerationStarted',
        properties: {
          correlationId,
          workflowId: request.workflowId,
          clientId: request.clientId,
          operation: 'generateCodeDb',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      // Use the workflowService to call db-first endpoint
      const response = await workflowService.generateDbCode(
        request.workflowId,
        request.stemWordsBlob,
        request.sampleKeywords
      );
      
      console.log('DB-first code generation response:', response);
      
      // Track successful API call in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'DbAPI_CodeGenerationCompleted',
          properties: {
            correlationId,
            workflowId: request.workflowId,
            moduleId: response.module_id,
            clientId: request.clientId,
            operation: 'generateCodeDb',
            responseStatus: response.status || 'unknown',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response;
    } catch (error) {
      console.error('DB-first code generation error:', error);
      
      // Track API error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in DB-first code generation'),
          properties: {
            correlationId,
            workflowId: request.workflowId,
            clientId: request.clientId,
            operation: 'generateCodeDb',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }

  // Get code generation status by workflow ID
  async getCodeGenerationStatusDb(workflowId: number, clientId?: number): Promise<any> {
    console.log(`Getting code generation status for workflow ID ${workflowId}`);
    
    // Track API call in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `db-codegen-status-${workflowId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_CodeGenerationStatusCheckStarted',
        properties: {
          correlationId,
          workflowId,
          clientId,
          operation: 'getCodeGenerationStatusDb',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      const response = await workflowService.getDbCodeGenerationStatus(workflowId, clientId);
      
      // Track status check result in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'DbAPI_CodeGenerationStatusCheckCompleted',
          properties: {
            correlationId,
            workflowId,
            clientId,
            operation: 'getCodeGenerationStatusDb',
            responseStatus: response.status || 'unknown',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response;
    } catch (error) {
      console.error('Error getting DB code generation status:', error);
      
      // Track error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in DB code gen status check'),
          properties: {
            correlationId,
            workflowId,
            clientId,
            operation: 'getCodeGenerationStatusDb',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }
  
  // Get code generation status by module ID (deprecated but maintained for backward compatibility)
  async getCodeGenerationStatusByModuleDb(moduleId: number): Promise<any> {
    console.log(`Getting code generation status for module ID ${moduleId} (deprecated method)`);
    console.warn('DEPRECATED: Using moduleId instead of workflowId. Please update your code.');
    
    // Track API call in Application Insights with deprecation warning
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_DeprecatedMethodUsed',
        properties: {
          moduleId,
          method: 'getCodeGenerationStatusByModuleDb',
          message: 'Using deprecated module-based method instead of workflow-based',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      const response = await workflowService.getDbCodeGenerationStatusByModule(moduleId);
      return response;
    } catch (error) {
      console.error('Error getting DB code generation status by module:', error);
      throw error;
    }
  }

  // Get code by workflow ID and module ID
  async getCodeDb(workflowId: number, moduleId: number, clientId?: number): Promise<any> {
    console.log(`Getting code for workflow ID ${workflowId}, module ID ${moduleId}`);
    
    try {
      const response = await workflowService.getDbCode(workflowId, moduleId, clientId);
      return response;
    } catch (error) {
      console.error('Error getting DB code:', error);
      throw error;
    }
  }
  
  // Get code by module ID (deprecated but maintained for backward compatibility)
  async getCodeByModuleDb(moduleId: number): Promise<any> {
    console.log(`Getting code for module ID ${moduleId} (deprecated method)`);
    console.warn('DEPRECATED: Using moduleId instead of workflowId. Please update your code.');
    
    // Track API call in Application Insights with deprecation warning
    const appInsights = this.getAppInsights();
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_DeprecatedMethodUsed',
        properties: {
          moduleId,
          method: 'getCodeByModuleDb',
          message: 'Using deprecated module-based method instead of workflow-based',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      const response = await workflowService.getDbCodeByModule(moduleId);
      return response;
    } catch (error) {
      console.error('Error getting DB code by module:', error);
      throw error;
    }
  }

  // Execute code using workflow ID and module ID
  async executeCodeDb(request: DbCodeExecutionRequest): Promise<any> {
    console.log('Executing code for workflow ID:', request.workflowId, 'module ID:', request.moduleId);
    
    // Track API call in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `db-execute-${request.workflowId}-${request.moduleId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_ExecuteCodeStarted',
        properties: {
          correlationId,
          workflowId: request.workflowId,
          moduleId: request.moduleId,
          clientId: request.clientId,
          operation: 'executeCodeDb',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      const response = await workflowService.executeDbCode(
        request.workflowId,
        request.moduleId,
        request.keywordsBlob,
        request.clientId
      );
      
      // Track successful API call in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'DbAPI_ExecuteCodeCompleted',
          properties: {
            correlationId,
            workflowId: request.workflowId,
            moduleId: request.moduleId,
            executionModuleId: response.execution_module_id,
            clientId: request.clientId,
            operation: 'executeCodeDb',
            responseStatus: response.status || 'unknown',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response;
    } catch (error) {
      console.error('Error executing DB code:', error);
      
      // Track API error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in DB code execution'),
          properties: {
            correlationId,
            workflowId: request.workflowId,
            moduleId: request.moduleId,
            clientId: request.clientId,
            operation: 'executeCodeDb',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }
  
  // Execute code using module ID (deprecated but maintained for backward compatibility)
  async executeCodeByModuleDb(request: DbCodeExecutionByModuleRequest): Promise<any> {
    console.log('Executing code with module ID:', request.moduleId, '(deprecated method)');
    console.warn('DEPRECATED: Using moduleId instead of workflowId. Please update your code.');
    
    // Track API call in Application Insights with deprecation warning
    const appInsights = this.getAppInsights();
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_DeprecatedMethodUsed',
        properties: {
          moduleId: request.moduleId,
          method: 'executeCodeByModuleDb',
          message: 'Using deprecated module-based method instead of workflow-based',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      const response = await workflowService.executeDbCodeByModule(
        request.moduleId,
        request.keywordsBlob
      );
      return response;
    } catch (error) {
      console.error('Error executing DB code by module:', error);
      throw error;
    }
  }

  // Get execution status by workflow ID
  async getExecutionStatusDb(workflowId: number, executionModuleId?: number, clientId?: number): Promise<any> {
    console.log(`Getting execution status for workflow ID ${workflowId}` + 
                (executionModuleId ? `, execution module ID ${executionModuleId}` : ''));
    
    // Track API call in Application Insights
    const appInsights = this.getAppInsights();
    const startTime = Date.now();
    const correlationId = `db-exec-status-${workflowId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_ExecutionStatusCheckStarted',
        properties: {
          correlationId,
          workflowId,
          executionModuleId,
          clientId,
          operation: 'getExecutionStatusDb',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      const response = await workflowService.getDbExecutionStatus(workflowId, executionModuleId, clientId);
      
      // Track status check result in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'DbAPI_ExecutionStatusCheckCompleted',
          properties: {
            correlationId,
            workflowId,
            executionModuleId,
            clientId,
            operation: 'getExecutionStatusDb',
            responseStatus: response.status || response.latest_status || 'unknown',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response;
    } catch (error) {
      console.error('Error getting DB execution status:', error);
      
      // Track error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in DB execution status check'),
          properties: {
            correlationId,
            workflowId,
            executionModuleId,
            clientId,
            operation: 'getExecutionStatusDb',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }
  
  // Get execution status by module ID (deprecated but maintained for backward compatibility)
  async getExecutionStatusByModuleDb(moduleId: number): Promise<any> {
    console.log(`Getting execution status for module ID ${moduleId} (deprecated method)`);
    console.warn('DEPRECATED: Using moduleId instead of workflowId. Please update your code.');
    
    // Track API call in Application Insights with deprecation warning
    const appInsights = this.getAppInsights();
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_DeprecatedMethodUsed',
        properties: {
          moduleId,
          method: 'getExecutionStatusByModuleDb',
          message: 'Using deprecated module-based method instead of workflow-based',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      const response = await workflowService.getDbExecutionStatusByModule(moduleId);
      return response;
    } catch (error) {
      console.error('Error getting DB execution status by module:', error);
      throw error;
    }
  }
}

// React Query hooks for database-first operations
import { useQuery, useMutation } from '@tanstack/react-query';

// Generate code with database workflow ID
export const useGenerateCodeDb = () => {
  return useMutation({
    mutationFn: (params: DbCodeGenerationRequest) => 
      Module2Service.getInstance().generateCodeDb(params),
  });
};

// Get code generation status by workflow ID
export const useGetCodeGenerationStatusDb = (workflowId: number, clientId?: number) => {
  return useQuery({
    queryKey: ['dbCodeGenStatus', workflowId, clientId],
    queryFn: () => Module2Service.getInstance().getCodeGenerationStatusDb(workflowId, clientId),
    enabled: !!workflowId,
    refetchInterval: 5000 // Poll every 5 seconds
  });
};

// Get code by workflow ID and module ID
export const useGetCodeDb = (workflowId: number, moduleId: number, clientId?: number) => {
  return useQuery({
    queryKey: ['dbCode', workflowId, moduleId, clientId],
    queryFn: () => Module2Service.getInstance().getCodeDb(workflowId, moduleId, clientId),
    enabled: !!(workflowId && moduleId),
  });
};

// Execute code with workflow ID and module ID
export const useExecuteCodeDb = () => {
  return useMutation({
    mutationFn: (params: DbCodeExecutionRequest) => 
      Module2Service.getInstance().executeCodeDb(params),
  });
};

// Get execution status by workflow ID
export const useGetExecutionStatusDb = (workflowId: number, executionModuleId?: number, clientId?: number) => {
  return useQuery({
    queryKey: ['dbExecutionStatus', workflowId, executionModuleId, clientId],
    queryFn: () => Module2Service.getInstance().getExecutionStatusDb(workflowId, executionModuleId, clientId),
    enabled: !!workflowId,
    refetchInterval: 3000 // Poll every 3 seconds
  });
};

// DEPRECATED hooks for backward compatibility

// Get code generation status by module ID (deprecated but maintained for backward compatibility)
export const useGetCodeGenerationStatusByModuleDb = (moduleId: number) => {
  console.warn('DEPRECATED: Using moduleId-based hook instead of workflowId-based. Please update your code.');
  return useQuery({
    queryKey: ['dbCodeGenStatusByModule', moduleId],
    queryFn: () => Module2Service.getInstance().getCodeGenerationStatusByModuleDb(moduleId),
    enabled: !!moduleId,
    refetchInterval: 5000 // Poll every 5 seconds
  });
};

// Get code by module ID (deprecated but maintained for backward compatibility)
export const useGetCodeByModuleDb = (moduleId: number) => {
  console.warn('DEPRECATED: Using moduleId-based hook instead of workflowId-based. Please update your code.');
  return useQuery({
    queryKey: ['dbCodeByModule', moduleId],
    queryFn: () => Module2Service.getInstance().getCodeByModuleDb(moduleId),
    enabled: !!moduleId,
  });
};

// Execute code by module ID (deprecated but maintained for backward compatibility)
export const useExecuteCodeByModuleDb = () => {
  console.warn('DEPRECATED: Using moduleId-based hook instead of workflowId-based. Please update your code.');
  return useMutation({
    mutationFn: (params: DbCodeExecutionByModuleRequest) => 
      Module2Service.getInstance().executeCodeByModuleDb(params),
  });
};

// Get execution status by module ID (deprecated but maintained for backward compatibility)
export const useGetExecutionStatusByModuleDb = (moduleId: number) => {
  console.warn('DEPRECATED: Using moduleId-based hook instead of workflowId-based. Please update your code.');
  return useQuery({
    queryKey: ['dbExecutionStatusByModule', moduleId],
    queryFn: () => Module2Service.getInstance().getExecutionStatusByModuleDb(moduleId),
    enabled: !!moduleId,
    refetchInterval: 3000 // Poll every 3 seconds
  });
};

// Create a module2Service with the methods we need
const baseService = Module2Service.getInstance();

// React Query hooks for simplified Module 2
// Reusing the useQuery and useMutation imports from above

/**
 * Direct integration hook - sends Module 1 data directly to Module 2's v4 endpoint
 * This is a specialized interface for the direct Module 1 to Module 2 integration button
 * Bypasses blob storage completely by sending actual data in memory
 */
export const useDirectModule1ToModule2 = () => {
  return useMutation({
    mutationFn: (params: {
      module1Results: any; // The complete Module 1 analysis results 
      workflowId: string;
      clientId?: string;
      keywords?: string[]; // Optional manual keywords if not in module1Results
    }) => {
      console.log('Direct Module 1 to Module 2 integration - sending in-memory data');
      
      // Extract topics from Module 1 results - transform to required format if needed
      let topicsForApi: any = {};
      
      if (Array.isArray(params.module1Results.topics)) {
        // Convert array format to nested object format expected by Module 2
        const topicsObj: any = {};
        params.module1Results.topics.forEach((topic: any) => {
          const subtopicsObj: any = {};
          
          // Convert subtopics array to object format
          (topic.subtopics || []).forEach((subtopic: any) => {
            subtopicsObj[subtopic.name] = subtopic.stem_words || [];
          });
          
          topicsObj[topic.name] = {
            subtopics: subtopicsObj
          };
        });
        
        topicsForApi = topicsObj;
      } else if (typeof params.module1Results.topics === 'object') {
        // Already in object format
        topicsForApi = params.module1Results.topics;
      }
      
      // Use provided keywords or extract from module1Results
      let keywordsForApi: string[] = [];
      
      // First try to use explicitly provided keywords
      if (params.keywords && Array.isArray(params.keywords) && params.keywords.length > 0) {
        keywordsForApi = params.keywords;
      } 
      // Then try to extract from module1Results
      else if (params.module1Results.keywords && Array.isArray(params.module1Results.keywords)) {
        keywordsForApi = params.module1Results.keywords.map((k: any) => 
          typeof k === 'string' ? k : k.text || k.keyword || k.toString()
        );
      }
      // If no keywords are found, throw error rather than using sample data
      else {
        console.error('No keywords found in module1Results or explicitly provided');
        throw new Error('No keywords available for classification. Please upload a keywords file or use "Load Latest Results" with a file that includes keywords.');
      }
      
      // Validate topics data
      if (!topicsForApi || Object.keys(topicsForApi).length === 0) {
        console.error('No topics found in Module 1 results', params.module1Results);
        throw new Error('No valid topics found in Module 1 results');
      }
      
      // Validate keywords data 
      if (!keywordsForApi || keywordsForApi.length === 0) {
        console.error('No valid keywords available for classification');
        throw new Error('No valid keywords available for classification');
      }
      
      console.log(`Sending ${keywordsForApi.length} keywords and ${Object.keys(topicsForApi).length} topics directly to Module 2`);
      console.log('Topics format for API:', topicsForApi);
      console.log('Sample keywords:', keywordsForApi.slice(0, 3));
      
      // Call v4 endpoint directly with the actual data, not blob paths
      return baseService.classifyKeywords(
        topicsForApi,
        keywordsForApi,
        params.workflowId,
        params.clientId
      );
    },
  });
};

// Classify keywords
export const useClassifyKeywords = () => {
  return useMutation({
    mutationFn: (params: {
      topics: any;
      keywords: string[];
      workflowId: string;
      clientId?: string;
    }) => baseService.classifyKeywords(
      params.topics,
      params.keywords,
      params.workflowId,
      params.clientId
    ),
  });
};

// Get classification status
export const useClassificationStatus = (classificationId: string, workflowId: string) => {
  return useQuery({
    queryKey: ['classificationStatus', classificationId, workflowId],
    queryFn: () => baseService.getClassificationStatus(classificationId, workflowId),
    enabled: !!(classificationId && workflowId),
    refetchInterval: 2000 // Poll every 2 seconds
  });
};

// List classification files
export const useClassificationFiles = (workflowId: string, clientId?: string) => {
  return useQuery({
    queryKey: ['classificationFiles', workflowId, clientId],
    queryFn: () => baseService.listClassificationFiles(workflowId, clientId),
    enabled: !!workflowId
  });
};

// Export the service instance
export default baseService;
